import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AppStateService } from '../../shared/services/app-state.service';
import { PerfilRecomendado } from '../../core/interface/perfil-recomendado';
import { HttpClientService } from '../../shared/services/http-client.service';
import { DistribuicaoCarteira, PerfilInvestidor } from '../../core/interface/perfil-investidor';
import { FormsModule } from '@angular/forms';
import { ModalExplicativoComponent } from "../../shared/components/modal-explicativo/modal-explicativo.component";
import { PerfilParticipante } from '../../core/interface/perfil-participante';
import { EnumStatusResponse } from '../../core/enum/EnumHttpResponse';
import { CampanhaPerfil } from '../../core/interface/campanha';
import { LoaderService } from '../../shared/services/loader.service';
import { ToastService } from '../../shared/services/toast.service';
import { RadioComponent } from '../../shared/components/radio/radio.component';
import { CheckboxComponent } from '../../shared/components/checkbox/checkbox.component';
import { QuestionarioRespondido, ExcluirRespostasPayload } from '../../core/interface/questionario';

@Component({
  selector: 'app-profile-selection',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalExplicativoComponent, RadioComponent, CheckboxComponent],
  templateUrl: './profile-selection.component.html',
  styleUrl: './profile-selection.component.scss',
})
export class ProfileSelection implements OnInit {
  token: string = '';
  exibirModalAjuda = false;
  perfilSelecionado?: number;
  perfilParticipante!: PerfilParticipante;
  campanha!: CampanhaPerfil;
  confirmouPerfilDiferente = false;
  perfisExpandidos: number[] = [];

  opcoesPerfil: PerfilInvestidor[] = [];
  perfilRecomendado?: PerfilRecomendado;

  questionarioRespondido!: QuestionarioRespondido[];

  constructor(
    private router: Router,
    private appStateService: AppStateService,
    private httpClientService: HttpClientService,
    private loaderService: LoaderService,
    private toastService: ToastService,
  ) { }

  ngOnInit(): void {
    this.token = this.appStateService.getToken()!;
    this.getPerfilParticipante(this.token);

    const recomendado = this.appStateService.getPerfilRecomendado();
    this.campanha = this.appStateService.getCampanha()!;

    if (recomendado) {
      this.perfilRecomendado = recomendado;
    }

    this.perfilSelecionado =
      this.appStateService.getSelectedProfile() ??
      this.perfilRecomendado?.id;
  }

  selectProfile(profileId: number): void {
    this.perfilSelecionado = profileId;

    this.appStateService.setSelectedProfile(profileId);

    this.confirmouPerfilDiferente = false;
  }

  /**
   * Habilita o botão "Seguinte" somente quando:
   * - há um perfil selecionado (radio); e
   * - se o perfil for diferente do recomendado, o checkbox de ciência estiver marcado.
   * O perfil recomendado avança sem exigir o checkbox (ele nem é exibido).
   */
  get podeAvancar(): boolean {
    if (!this.perfilSelecionado) {
      return false;
    }

    if (this.perfilSelecionado === this.perfilRecomendado?.id) {
      return true;
    }

    return this.confirmouPerfilDiferente;
  }

  openHelpModal(): void {
    this.exibirModalAjuda = true;
  }

  closeHelpModal(): void {
    this.exibirModalAjuda = false;
  }

  goBack(): void {
    const payload: ExcluirRespostasPayload = {
      ids: this.questionarioRespondido?.map(q => q.idRespostaPartic) ?? []
    };

    if(this.perfilParticipante?.id) {
      this.deletedQuestionnaire(this.token, this.perfilParticipante.id, payload);
    }
  }

  confirmOptions(): void {
    if (this.perfilSelecionado) {
      this.appStateService.setSelectedProfile(this.perfilSelecionado);
      this.router.navigate(['/term']);
    }
  }

  toggleDistribuicao(idPerfil: number): void {
    const index = this.perfisExpandidos.indexOf(idPerfil);

    if (index > -1) {
      this.perfisExpandidos.splice(index, 1);
    } else {
      this.perfisExpandidos.push(idPerfil);
    }
  }

  getGradient(distribuicao: DistribuicaoCarteira[]): string {
    if (!distribuicao || distribuicao.length === 0) {
      return 'conic-gradient(#e5e7eb 0% 100%)';
    }

    let acumulado = 0;

    const stops = distribuicao
      .filter(item => item.percentual > 0)
      .map(item => {
        const inicio = acumulado;
        acumulado += item.percentual;

        const cor = this.isValidColor(item.cor) ? item.cor : '#e5e7eb';

        return `${cor} ${inicio}% ${acumulado}%`;
      });

    if (stops.length === 0) {
      return 'conic-gradient(#e5e7eb 0% 100%)';
    }

    return `conic-gradient(${stops.join(', ')})`;
  }

  private isValidColor(color: string): boolean {
    return /^#([0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(color);
  }

  private getPerfilParticipante(token: string): void {
    this.loaderService.showLoader();
    this.httpClientService.getPerfilParticipante(token)
    .then(response => {
        if (response.status === EnumStatusResponse.Success) {
          const body = response.body;
          let participante: PerfilParticipante | undefined;
          if (Array.isArray(body)) {
            participante = body[0] as PerfilParticipante;
          } else {
            participante = body as PerfilParticipante;
          }

          if (participante) {
            this.perfilParticipante = participante;
            this.appStateService.setPerfilParticipante(this.perfilParticipante);

            const perfilRecomendado: PerfilRecomendado = {
              id: participante.idPerfilRecomendado,
              titulo: participante.perfilRecomendado,
              pontuacao: participante.pontuacaoTotal
            };

            this.perfilRecomendado = perfilRecomendado;
            this.appStateService.setPerfilRecomendado(perfilRecomendado);

            // Mantém a seleção salva ou assume o perfil recomendado.
            this.perfilSelecionado =
              this.appStateService.getSelectedProfile() ?? perfilRecomendado.id;

            // Respostas e opções só são buscadas após o participante/recomendado
            // estarem disponíveis (o sort das opções depende do recomendado).
            this.getQuestionnaireParticipante(token, participante.id);
            this.getProfileOptions(token, this.campanha.idCampanha);
          }
        }
      })
      .catch(error => {
        console.error('Participante não encontrado para os parâmetros fornecidos.', error);
        this.toastService.show(error?.message ?? 'Não foi possível carregar os dados do participante.', 'error');
        const detail: string | undefined = error?.problem?.detail;
        if (detail?.trim()) {
          this.appStateService.setMensagemNaoApto(detail.trim());
        }
        this.router.navigate(['/not-found']);
      })
      .finally(() => this.loaderService.hideLoader());
  }

  private getProfileOptions(token: string, idCampanha: number): void {
    this.loaderService.showLoader();
    this.httpClientService.getPerfisInvestidor(token, idCampanha)
      .then(response => {
        if (response.status !== EnumStatusResponse.Success || !response.body) {
          return;
        }

        const perfis = response.body as PerfilInvestidor[];

        // Remove duplicidades por idPerfilInvestimento (mantém a 1ª ocorrência),
        // garantindo que o perfil recomendado apareça uma única vez.
        const perfisUnicos = perfis.filter(
          (perfil, index, lista) =>
            lista.findIndex(p => p.idPerfilInvestimento === perfil.idPerfilInvestimento) === index
        );

        // Apenas ordena o perfil recomendado para o topo (sem inseri-lo manualmente).
        this.opcoesPerfil = perfisUnicos.sort(
          (a, b) => {
            if (a.idPerfilInvestimento === this.perfilRecomendado?.id) return -1;
            if (b.idPerfilInvestimento === this.perfilRecomendado?.id) return 1;
            return 0;
          }
        );
      })
      .catch(error => {
        console.error(
          'Erro ao carregar os perfis de investidor:',
          error
        );
        this.toastService.show('Não foi possível carregar os perfis de investidor.', 'error');
      })
      .finally(() => this.loaderService.hideLoader());
  }

  private getQuestionnaireParticipante(token: string, idParticipante: number): void {
    this.loaderService.showLoader();
    this.httpClientService.getQuestionnaireParticipante(token, idParticipante)
      .then(response => {
        if (response.status !== EnumStatusResponse.Success || !response.body) {
          return;
        }
        const questionarioRespondido = response.body as QuestionarioRespondido[];
        this.questionarioRespondido = questionarioRespondido;
        this.appStateService.setQuestionarioRespondidoParticipante(questionarioRespondido);

      })
      .catch(error => {
        console.error(
          'Erro ao carregar os questionários do participante:',
          error
        );
        this.toastService.show('Não foi possível carregar os questionários do participante.', 'error');
      })
      .finally(() => this.loaderService.hideLoader());
  }

  private deletedQuestionnaire(token: string, idParticipante: number, payload: ExcluirRespostasPayload): void {
    this.loaderService.showLoader();
    this.httpClientService.deletedQuestionnaire(token, idParticipante, payload)
      .then(response => {
        if (response.status !== EnumStatusResponse.Success || !response.body) {
          return;
        }

        this.router.navigate(['questionnaire'], {
          state: {
            perfilSelecionado: this.perfilSelecionado,
          },
        });
      })
      .catch(error => {
        console.error(
          'Erro ao deletar o questionário do participante:',
          error
        );
        this.toastService.show('Não foi possível deletar o questionário do participante.', 'error');
      })
      .finally(() => this.loaderService.hideLoader());
  }
}
